import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from 'components/UI/Button';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Divider from 'components/UI/Divider';
import CurrencyInput from 'components/UI/CurrencyInput';
import ConnectionStatusBadge from 'components/Company/ConnectionStatusBadge';
import CreditActionButtons from 'components/Company/CreditActionButtons';
import { Space } from 'styles/themeSpaces';
import { Text, Border, Background, Main } from 'styles/themeColors';
import { currencySign } from 'utils/formatters';
import {
  MDMCreditCardProps,
} from 'types/credit.types';
import {
  CREDIT_LABELS,
  CREDIT_DESCRIPTIONS,
  CREDIT_ERROR_MESSAGES,
  CREDIT_PLACEHOLDERS,
  CREDIT_STATUS_MESSAGES,
  CREDIT_BUTTONS
} from 'constants/credit';
import {
  getCreditStatus,
  getCreditStatusBgColor,
  shouldShowCreditInput,
  shouldShowTrackManuallyButton,
  CREDIT_VALUE_RANGE
} from 'utils/credit';
import { getCurrencyIsoByCountry, getCurrencySymbol } from 'utils/country';

const TextSecondary = styled.span`
  color: ${Text.secondary} !important;
  margin-top: ${Space.spBasePlus};
`;

const CreditInfoRow = styled(FlexboxContainer)`
  padding: ${Space.sp2x} 0;
`;

const CreditLabel = styled.span`
  font-weight: normal;
`;

const CreditValue = styled.span`
  font-weight: normal;
`;


const MDMCreditCard: React.FC<MDMCreditCardProps> = ({
  className,
  isConnected,
  creditInfo,
  onUpdateCredit,
  onTrackManuallyClick,
  creditTrackingMode,
  vendorCompanyDetails
}) => {
  const [value, setValue] = useState(creditInfo.totalCreditLimit.toFixed(2));
  const [isInvalid, setIsInvalid] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setValue(creditInfo.totalCreditLimit.toFixed(2));
  }, [creditInfo.totalCreditLimit]);

  const isSystemOffline = creditInfo?.companyMdmDetails?.idStatus?.toUpperCase() !== 'ACTIVE';

  const creditStatus = getCreditStatus(creditTrackingMode, isSystemOffline);
  const creditStatusBgColor = getCreditStatusBgColor(creditTrackingMode, isSystemOffline);
  const color = creditStatusBgColor === Main.warning ? Main.black : Main.white;

  const onInputChange = (value?: string) => {

    if (isNaN(Number(value))) {
      return;
    }

    const val = value ? value : '0';
    // Validate non-zero positive number and max value is 999,000,000
    setIsInvalid(+val <= CREDIT_VALUE_RANGE.MIN || +val > CREDIT_VALUE_RANGE.MAX); // CREDIT_VALUE_RANGE.MIN || +val > 999000000); --- IGNORE ---
    setValue(val);
    setDirty(true);
  };

  const onCancel = () => {
    const val = creditInfo.totalCreditLimit ? creditInfo.totalCreditLimit.toFixed(2) : '0'
    setIsInvalid(+val <= CREDIT_VALUE_RANGE.MIN || +val > CREDIT_VALUE_RANGE.MAX); // CREDIT_VALUE_RANGE.MIN || +val > 999000000); --- IGNORE ---
    setValue(val);
    setDirty(false);
  };

  const onApplyCreditLimit = () => {
    setDirty(true);

    if (value) {
      onUpdateCredit(value);
    }
  };

  const showCreditInput = shouldShowCreditInput(creditTrackingMode);
  const showTrackManuallyButton = shouldShowTrackManuallyButton(creditTrackingMode, vendorCompanyDetails.creditId!);

  const countryCode = vendorCompanyDetails?.address?.countryIsoCode;
  const utilization = currencySign(countryCode, creditInfo?.utilization);
  const available = currencySign(countryCode, creditInfo?.availableCreditLimit);
  const total = currencySign(countryCode, creditInfo?.totalCreditLimit);

  const currencyPrefix = getCurrencySymbol(getCurrencyIsoByCountry(countryCode));

  return (
    <FlexboxContainer
      className={`border ${className}`}
      width="50%"
      flexDirection="column"
      padding={Space.sp2x}
      borderColor={`${Border.light} !important`}
      bgColor={Background.light}
      height='fit-content'
    >
      <FlexboxContainer justifyContent="flex-start" alignItems="center">
        <h5>{CREDIT_LABELS.CREDIT_CHECK}</h5>
        <ConnectionStatusBadge
          status={creditStatus}
          backgroundColor={creditStatusBgColor}
          color={color}
        />
      </FlexboxContainer>

      <TextSecondary className="dense-italic">
        {CREDIT_DESCRIPTIONS.CREDIT_CHECK_DESCRIPTION}
      </TextSecondary>

      <FlexboxContainer
        className="border"
        flexDirection="column"
        bgColor={Background.shaded}
        borderColor={`${Border.light} !important`}
        margin={`${Space.spBasePlus} 0`}
        padding={`${Space.sp2x} ${Space.spBase} ${Space.sp2x} ${Space.sp2x}`}
      >
        {showCreditInput ? (
          <FlexboxContainer
            flexDirection="column"
            padding={'0px'}
          >
            <div className="base" style={{ marginBottom: Space.spBase }}>{CREDIT_LABELS.CREDIT_LIMIT}</div>
            <FlexboxContainer
              justifyContent="space-between"
              alignItems="baseline"
              gap={Space.spBase}
              padding={'0px'}
            >
              <CurrencyInput
                required
                selectAllOnFirstClick
                id='mdm-credit-card-limit-input-id'
                prefix={currencyPrefix}
                textAlign='right'
                isInvalid={isInvalid}
                placeholder={CREDIT_PLACEHOLDERS.ENTER_AMOUNT}
                decimalsLimit={2}
                value={value}
                onValueChange={onInputChange}
                errorMessage={value && isInvalid ? CREDIT_ERROR_MESSAGES.CREDIT_LIMIT : ''}
                width="100%"
                dirty={dirty}
                padding='0px'
              />
              <CreditActionButtons
                onCancel={onCancel}
                onApply={onApplyCreditLimit}
                disabled={!dirty || isInvalid}
              />
            </FlexboxContainer>
          </FlexboxContainer>
        ) : (
          <CreditInfoRow justifyContent="space-between" alignItems="center" padding={`0 0 ${Space.sp2x}`}>
            <CreditLabel className="base">{CREDIT_LABELS.CREDIT_LIMIT}</CreditLabel>
            <CreditValue className="base">
              {isConnected ? total : CREDIT_STATUS_MESSAGES.NOT_AVAILABLE}
            </CreditValue>
          </CreditInfoRow>
        )}

        {!showCreditInput ? <Divider bgColor={Border.main} /> : null}

        <CreditInfoRow justifyContent="space-between">
          <CreditLabel className="base">{CREDIT_LABELS.UTILIZATION}</CreditLabel>
          <CreditValue className="base">{isConnected ? utilization : CREDIT_STATUS_MESSAGES.NOT_AVAILABLE}</CreditValue>
        </CreditInfoRow>

        <FlexboxContainer justifyContent="space-between" padding={`${Space.sp2x} 0 0`}>
          <CreditLabel className="base">{CREDIT_LABELS.AVAILABLE}</CreditLabel>
          <CreditValue className="base">
            {isConnected ? available : CREDIT_STATUS_MESSAGES.NOT_AVAILABLE}
          </CreditValue>
        </FlexboxContainer>
      </FlexboxContainer>

      {showTrackManuallyButton && onTrackManuallyClick ? (
        <FlexboxContainer justifyContent="flex-end" margin={`${Space.spBase} 0 0 0`}>
          <Button
            onClick={onTrackManuallyClick}
            borderColor={Main.brandDark}
            bgColor={Main.brandDark}
          >
            {CREDIT_BUTTONS.TRACK_MANUALLY_OFMP}
          </Button>
        </FlexboxContainer>
      ) : null}
    </FlexboxContainer>
  );
};


export default MDMCreditCard;